import { createTheme } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import React, { useState } from 'react';
/**
 * Create a theme instance
 * @returns {object} Material UI theme object
 */
const createAppTheme = () => {
  return createTheme({
    palette: {
      mode: 'light',
      primary: {
        main: '#1A0146', 
        light: '#1A0146',
        background: '#1A0146',
        dark: '#1A0146',
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: '#000000',
        light: '#4DABF5',
        dark: '#1976D2',
        contrastText: '#FFFFFF',
      },
      error: {
        main: '#F44336',
      },
      warning: {
        main: '#ff0000ff',
      },
      info: {
        main: '#2196F3',
      },
      success: {
        main: '#4CAF50',
      },
      pending: {
        main: '#6E6E6E',
        light: '#EFEFEF',
        dark: '#555555',
        contrastText: '#FFFFFF',
      },
      background: {
        default: '#FFFFFF',
        paper: '#FFFFFF',
      },
      text: {
        primary: '#000000',
        secondary: 'rgba(0, 0, 0, 0.6)',
        disabled: 'rgba(0, 0, 0, 0.38)',
      },
    },
    shape: {
      borderRadius: 6,
    },
    components: {
      // You can override specific component styles here
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '6px',
            boxShadow: 'none',
            textTransform: 'none', // Prevent uppercase text
            '&:hover': {
              boxShadow: 'none',
              backgroundColor: '#444DFF', // Set hover color
              color: '#FFFFFF',
            },
          },
          action: {
            alignItems: 'center',
            display: 'flex',
            marginTop: '0px',
          },
          contained: {
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none',
              backgroundColor: '#1A0146', // Set hover color for contained buttons
            },
            '&:active': {
              boxShadow: 'none',
            },
          },
            containedSecondary: {
              backgroundColor: '#DEDCE3', 
              color: 'black',
              '&:hover': {
                backgroundColor: '#CFCBD4', 
                color: 'black',
              }, 
            },
          outlined: {
            '&:hover': {
              backgroundColor: 'rgba(68, 77, 255, 0.04)', // Lighter hover effect for outlined buttons
              borderColor: '#611DDE',
              color: '#611DDE',
            },
          },
          text: {
            '&:hover': {
              backgroundColor: '#611DDE', // Lighter hover effect for text buttons
              color: '#611DDE',
            },
          },
        },
        defaultProps: {
          disableElevation: true, // This removes the elevation/shadow effect
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            boxShadow: '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)',
          },
        },
      },
      MuiChip: {
        variants: [
          {
            props: { variant: 'status', color: 'warning' },
            style: {
              backgroundColor: '#FFF5F5',
              color: 'black',
              border: '1px solid #FFD6D6',
              borderRadius: '11px',
              fontSize: '16px',
              padding: '4px 6px',
              gap: '8px',
              '& .MuiChip-icon': {
                color: '#FF4040',
              },
            },
          },
          {
            props: { variant: 'status', color: 'info' },
            style: {
              backgroundColor: '#4b51f813',
              color: 'black',
              border: '1px solid #4b51f884',
              borderRadius: '11px',
              fontSize: '16px',
              padding: '4px 6px',
              gap: '8px',
            },
          },
          {
            props: { variant: 'status', color: 'success' },
            style: {
              backgroundColor: '#F3FFF5',
              color: 'black',
              border: '1px solid #00b54e95',
              borderRadius: '11px',
              fontSize: '16px',
              padding: '4px 6px',
              gap: '8px',
            },
          },
          {
            props: { variant: 'status', color: 'pending' },
            style: {
              backgroundColor: '#F5F8FF',
              color: 'black',
              borderRadius: '11px',
              padding: '4px 6px',
              fontSize: '16px',
              gap: '8px',
              border: '1px solid #d6d6d6',
              '& .MuiChip-icon': {
                color: '#6E6E6E',
              },
            },
          },
        ],
        styleOverrides: {
          root: {
            height: 'auto',
            width: 'fit-content',
            display: 'inline-flex',
            borderRadius: '11px',
            padding: '4px',
            gap: '6px',
            color: 'black',
            '& .MuiChip-label': {
              paddingLeft: 4,
              paddingRight: 4,
              whiteSpace: 'normal',
              fontSize: '0.875rem',
              display: 'inline-block',
            },
            '& .MuiChip-icon': {
              marginLeft: 4,
              display: 'flex',
              alignItems: 'center',
            },
          },
        },
      },
      MuiAlert: {
        defaultProps: {
          iconMapping: {
            success: <img src="/icon/sample-approvals/success-icon.svg" style={{width:16}} />,
            warning: <img src="/icon/sample-approvals/danger-icon.svg" alt="Error" style={{ width: 16 }} />,
            info: <img src="/icon/sample-approvals/info-icon-blue.svg" alt="Info" style={{ width: 16}} />,
            error: <img src="/icon/sample-approvals/danger-icon.svg" alt="Error" style={{ width: 16 }} />,
          },
        },
        styleOverrides: {
          root: {
            borderRadius: '8px',
            fontWeight: 500,
            fontSize: '1.08rem',
            alignItems: 'center',
            minHeight: '48px',
            borderWidth: '2px',
            borderStyle: 'solid',
            boxShadow: 'none !important',
            WebkitBoxShadow: 'none',
            MozBoxShadow: 'none',
          },
          standardInfo: {
            backgroundColor: '#f5f7ff',
            color: '#4B50F8',
            boxShadow: 'none !important',
            fontSize: '14px',
            border: '1px solid #4b51f894',
          },
          standardSuccess: {
            backgroundColor: '#f5fff762',
            color: '#00B54F',
            fontSize: '14px',
            border: '1px solid #00b54e8a',
            boxShadow: 'none !important',
          },
          standardWarning: {
            backgroundColor: '#ff41410c',
            color: '#DE0000',
            borderColor: '#de00001b',
            fontSize: '14px',
            boxShadow: 'none !important',
          },
            standardError: {
            backgroundColor: '#ffe9e9ff',
            color: '#DE0000',
            borderColor: '#ff434332',
            fontSize: '14px',
            boxShadow: 'none !important',
          },
        },
      },
    },
  });
};

export default createAppTheme;
